import React from 'react'
import type { CardWriteHandler } from '../../types/cardWrite'
import { Box } from '@mui/material'
import { DashboardRow, DashboardCell, DashboardConfig } from '../../types/gotypes'
import { calculateCellWidths } from '../../utils/dashboard'
import { STACK_BREAKPOINT, resolveRowHeight } from '../../utils/dashboardBuilderLayout'
import DashboardCellComponent from './DashboardCellComponent'

interface DashboardRowComponentProps {
  row: DashboardRow
  customer?: string
  job?: string
  dashboardId?: string
  readOnly?: boolean
  /** #1219 gate 2 — see DashboardLayoutComponent.tsx's doc. */
  canRegenerate?: boolean
  globalFilter?: string
  jobFilters?: Record<string, string>
  colorMap?: Record<string, string>
  themeColorMaps?: Record<string, Record<string, string>>
  dashboardConfig?: DashboardConfig
  currentPageIndex?: number
  rowIndex?: number
  onCellUpdate?: (cellIndex: number, cell: DashboardCell) => void | Promise<void>
  /** #1256: narrow card text/sign-off write for any logged-in user; passed straight through. */
  onCardWrite?: CardWriteHandler
  /** See DashboardLayoutComponent.tsx's doc — undefined at the top level. */
  pathPrefix?: string
  reviewMode?: boolean
  changedWidgetPaths?: Set<string>
}

const DashboardRowComponent: React.FC<DashboardRowComponentProps> = ({
  row,
  customer,
  job,
  dashboardId,
  readOnly,
  canRegenerate,
  globalFilter,
  jobFilters,
  colorMap,
  themeColorMaps,
  dashboardConfig,
  currentPageIndex = 0,
  rowIndex = 0,
  onCellUpdate,
  onCardWrite,
  pathPrefix,
  reviewMode,
  changedWidgetPaths,
}) => {
  const widths = calculateCellWidths(row.cells)
  // The height the row was dragged to, never below the tallest widget's floor —
  // the same number the editor renders, so what was arranged is what ships.
  const height = resolveRowHeight(row)
  // #969 Part F: the same address scheme dashboardWidgetWalk.ts computes —
  // `${pageIndex}-${rowIndex}-${cellIndex}` at the top, with a
  // `-${rowIndex}-${cellIndex}` suffix per nesting level.
  const rowPathBase = pathPrefix ?? String(currentPageIndex)

  return (
    <Box
      id={`row-${currentPageIndex}-${rowIndex}`}
      sx={{
        display: 'flex',
        // Below the breakpoint a row's widgets stack into one column, in order.
        // That is the whole responsive rule: no reflow, no reordering.
        flexDirection: { xs: 'column', [STACK_BREAKPOINT]: 'row' },
        alignItems: 'stretch',
        width: '100%',
        minHeight: `${height}px`,
      }}
    >
      {row.cells.map((cell, index) => (
        <DashboardCellComponent
          key={index}
          cell={cell}
          width={widths[index]}
          customer={customer}
          job={job}
          dashboardId={dashboardId}
          readOnly={readOnly}
          canRegenerate={canRegenerate}
          globalFilter={globalFilter}
          jobFilters={jobFilters}
          colorMap={colorMap}
          themeColorMaps={themeColorMaps}
          dashboardConfig={dashboardConfig}
          currentPageIndex={currentPageIndex}
          cellPath={`${rowPathBase}-${rowIndex}-${index}`}
          reviewMode={reviewMode}
          changedWidgetPaths={changedWidgetPaths}
          onCellUpdate={onCellUpdate ? (updatedCell) => onCellUpdate(index, updatedCell) : undefined}
          onCardWrite={onCardWrite}
        />
      ))}
    </Box>
  )
}

export default DashboardRowComponent
